import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { getClosedTradeNetPL } from '../utils/tradeUtils';

function toDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  if (dateStr instanceof Date) {
    return new Date(dateStr.getFullYear(), dateStr.getMonth(), dateStr.getDate());
  }
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function getCalendarDays(monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const startDay = new Date(year, month, 1).getDay();
  const start = new Date(year, month, 1 - startDay);
  const today = new Date();
  const todayKey = toDateKey(today);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const inMonth = d.getMonth() === month && d.getFullYear() === year;
    days.push({
      date: d,
      key: toDateKey(d),
      inMonth,
      isToday: toDateKey(d) === todayKey,
      dayNum: d.getDate(),
    });
  }
  return days;
}

function defaultFormatCurrency(value) {
  const v = parseFloat(value) || 0;
  const sign = v >= 0 ? '+' : '';
  return `${sign}$${v.toFixed(0)}`;
}

function defaultGetPLColor(value) {
  if (value > 0) return 'success';
  if (value < 0) return 'error';
  return 'default';
}

export default function MonthPLCalendar({
  data = [],
  formatCurrency = defaultFormatCurrency,
  getPLColor = defaultGetPLColor,
}) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const plByDate = useMemo(() => {
    const map = {};
    for (const row of data) {
      const trades = row.trades || [];
      for (const trade of trades) {
        const tradeDate = trade.closingDate || trade.tradeDate;
        const d = parseLocalDate(tradeDate);
        if (!d) continue;
        const key = toDateKey(d);
        const tradeType = (trade.tradeType || trade.trade_type || '').toUpperCase();
        const isDividend = tradeType === 'DIVIDEND' || tradeType === 'DIV';
        const tradeDiv = parseFloat(trade.dividend || trade.dividend_amount || 0) || 0;
        const closed = isDividend
          ? 0
          : getClosedTradeNetPL(trade);
        if (!map[key]) map[key] = { closed: 0, div: 0 };
        map[key].closed += closed;
        map[key].div += tradeDiv;
      }
    }
    return map;
  }, [data]);

  const monthPL = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    let closed = 0;
    let div = 0;
    for (const [key, value] of Object.entries(plByDate)) {
      const d = parseLocalDate(key);
      if (d && d.getFullYear() === year && d.getMonth() === month) {
        closed += value.closed;
        div += value.div;
      }
    }
    return { closed, div, total: closed + div };
  }, [plByDate, currentMonth]);

  const monthLabel = currentMonth.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
  const calendarDays = useMemo(() => getCalendarDays(currentMonth), [currentMonth]);

  if (data.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">No closed position data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setCurrentMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
        >
          &lt; Prev
        </Button>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6">{monthLabel}</Typography>
          <Typography variant="caption" color="text.secondary">
            Month P/L: {' '}
            <Box
              component="span"
              sx={{
                color: monthPL.total >= 0 ? 'success.main' : 'error.main',
                fontWeight: 'bold',
              }}
            >
              {formatCurrency(monthPL.total)}
            </Box>
          </Typography>
          {Math.abs(monthPL.div) >= 0.005 && (
            <Typography variant="caption" color="text.secondary" display="block">
              Closed {formatCurrency(monthPL.closed)} · Div {formatCurrency(monthPL.div)}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setCurrentMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
        >
          Next &gt;
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ p: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => (
            <Box key={d} sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '0.75rem' }}>
              {d}
            </Box>
          ))}
          {calendarDays.map(day => {
            const entry = plByDate[day.key];
            const hasPL = !!entry && (Math.abs(entry.closed) >= 0.005 || Math.abs(entry.div) >= 0.005);
            const closed = entry?.closed || 0;
            const div = entry?.div || 0;
            const hasClosed = Math.abs(closed) >= 0.005;
            const hasDiv = Math.abs(div) >= 0.005;
            const mainVal = hasClosed ? closed : div;
            return (
              <Box
                key={day.key}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0.5,
                  p: 0.75,
                  minHeight: 72,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  opacity: day.inMonth ? 1 : 0.35,
                  bgcolor: day.isToday ? 'action.hover' : 'background.paper',
                }}
              >
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ textAlign: 'center', color: 'text.secondary', width: '100%' }}
                >
                  {day.dayNum}
                </Typography>
                {hasPL && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                    <Typography
                      display="block"
                      sx={{
                        textAlign: 'center',
                        color: mainVal >= 0 ? 'success.main' : 'error.main',
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        width: '100%',
                      }}
                    >
                      {formatCurrency(mainVal)}
                    </Typography>
                    {hasClosed && hasDiv && (
                      <Typography
                        display="block"
                        sx={{
                          textAlign: 'center',
                          color: 'text.secondary',
                          fontSize: '0.65rem',
                          width: '100%',
                        }}
                      >
                        {formatCurrency(div)} div
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
}
